import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import {
  useApproveRefundLine,
  useGetPickupRefundRequests,
  useConfirmManualRefundPayout,
  useReconcileManualRefundPayout,
  useReconcileRefundSettlementIssue,
  type ManualRefundPayoutRow,
  type PickupRefundRequestRow,
  type PickupRefundRequestStatus,
  type RefundActionEventRow,
  type RefundRequestLineRow,
  type RefundRequestSource,
} from "@/api/pickup-refund-requests";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Wallet,
} from "lucide-react";
import { formatCents } from "@/api/carrier-rate-bands";
import {
  useAllCarrierOptions,
  useCarrierScope,
  useCenterScopeNotApplicable,
} from "@/contexts/carrier-scope-context";
import { useSession } from "@/services/auth0/session";
import {
  apiErrorMessage,
  REFUND_CHARGE_LABEL,
  RefundLineActionDialog,
  type RefundLineAction,
  reportRefundLineSettlement,
  settlementMessage,
} from "@/features/refunds/RefundLineActionDialog";

/**
 * Phase 11c (#233) / #261: admin queue for refund requests.
 *
 * Originally pickup-only. As of #261 it also surfaces NEGATIVE_BALANCE
 * tickets created server-side when an order's balance crosses below zero
 * on a paid order (SLA-miss credit, goodwill, volume rebate, etc.).
 *
 * Source column tells the admin at a glance which kind of ticket they're
 * looking at. Approve hits the same Stripe partial-refund machinery either
 * way — for pickup sources via the booking's funding payments, for
 * NEGATIVE_BALANCE via the linked order's funding payments.
 */

const STATUS_VARIANT: Record<
  PickupRefundRequestStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  PENDING: "default",
  APPROVED: "secondary",
  PARTIALLY_APPROVED: "outline",
  DENIED: "destructive",
};

const STATUS_LABEL: Record<PickupRefundRequestStatus, string> = {
  PENDING: "Pending",
  APPROVED: "Approved",
  PARTIALLY_APPROVED: "Partially approved",
  DENIED: "Denied",
};

const SOURCE_LABEL: Record<RefundRequestSource, string> = {
  PICKUP_DETACH: "Pickup detach",
  PICKUP_CANCEL: "Pickup cancel",
  PICKUP_RESCHEDULE: "Pickup reschedule",
  NEGATIVE_BALANCE: "Negative balance",
  SHIPMENT_CANCELLATION: "Cancellation",
  ADMIN_ORDER_REFUND: "Admin-created",
};

const SOURCE_VARIANT: Record<
  RefundRequestSource,
  "default" | "secondary" | "destructive" | "outline"
> = {
  PICKUP_DETACH: "outline",
  PICKUP_CANCEL: "outline",
  PICKUP_RESCHEDULE: "outline",
  NEGATIVE_BALANCE: "destructive",
  SHIPMENT_CANCELLATION: "default",
  ADMIN_ORDER_REFUND: "secondary",
};

type SourceFilter = "ALL" | RefundRequestSource;

const LINE_STATUS_VARIANT: Record<
  RefundRequestLineRow["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  PENDING: "default",
  APPROVED: "secondary",
  DENIED: "destructive",
};

function lineDecider(line: RefundRequestLineRow): string | null {
  // Ticket 09: the explicit decider wins; emails add the identity.
  if (line.deciderKind === "SYSTEM")
    return `rule: ${line.deciderRule ?? "unnamed"}`;
  if (line.deciderKind === "UNRECORDED") return "decider not recorded";
  if (line.approvedByAdmin) return `${line.approvedByAdmin.email} (admin)`;
  if (line.approvedByCarrier)
    return `${line.approvedByCarrier.email} (carrier)`;
  if (line.status !== "PENDING") return "decider not recorded";
  return null;
}

function customerName(
  c: PickupRefundRequestRow["requestedByCustomer"]
): string {
  if (!c) return "System";
  const full = [c.firstName, c.lastName].filter(Boolean).join(" ");
  return full || c.email;
}

function requesterName(row: PickupRefundRequestRow): string {
  if (row.requestedByCustomer) return customerName(row.requestedByCustomer);
  const admin = row.requestedByInternalUser;
  if (!admin) return "Automated system";
  return (
    [admin.firstName, admin.lastName].filter(Boolean).join(" ") || admin.email
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function originSummary(r: PickupRefundRequestRow): {
  primary: string;
  secondary: string;
} {
  if (r.source === "NEGATIVE_BALANCE" && r.order) {
    return {
      primary: `Order #${r.order.orderNumber}`,
      secondary: r.priceAdjustment
        ? `${r.priceAdjustment.type} · ${(
            r.priceAdjustment.amountCents / 100
          ).toFixed(2)} CAD`
        : `Balance: ${(r.order.balanceCents / 100).toFixed(2)} CAD`,
    };
  }
  if (r.pickupBooking) {
    return {
      primary: `${formatDate(r.pickupBooking.scheduledDate)} · ${r.pickupBooking.slot.name}`,
      secondary: r.pickupBooking.pickupAddress.city,
    };
  }
  return { primary: "—", secondary: "" };
}

export function PickupRefundRequestsScreen() {
  // Refund requests are payment-side records — they trace back to a booking
  // or shipment but the dispatch-center axis doesn't make sense here.
  // Carrier-scoped only.
  useCenterScopeNotApplicable();
  useAllCarrierOptions();
  const { carrierIds } = useCarrierScope();
  const { user } = useSession();
  const [searchParams] = useSearchParams();
  const requestedRefundId = searchParams.get("requestId");
  const [statusFilter, setStatusFilter] =
    useState<PickupRefundRequestStatus>("PENDING");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("ALL");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [lineAction, setLineAction] = useState<RefundLineAction | null>(null);
  const [payoutAction, setPayoutAction] =
    useState<ManualRefundPayoutRow | null>(null);
  const [settlementAction, setSettlementAction] =
    useState<RefundActionEventRow | null>(null);
  const approveLine = useApproveRefundLine();

  const {
    data: rows,
    isLoading,
    refetch,
  } = useGetPickupRefundRequests({
    status: requestedRefundId ? "PENDING" : statusFilter,
    source:
      requestedRefundId || sourceFilter === "ALL" ? undefined : sourceFilter,
    carrierIds: requestedRefundId ? undefined : carrierIds,
    limit: requestedRefundId ? 200 : undefined,
  });

  useEffect(() => {
    if (
      !requestedRefundId ||
      !rows?.some((row) => row.id === requestedRefundId)
    ) {
      return;
    }
    setExpanded((current) =>
      current[requestedRefundId]
        ? current
        : { ...current, [requestedRefundId]: true }
    );
  }, [requestedRefundId, rows]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Wallet className="h-5 w-5" />
          Refund Requests
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          className="h-8"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        One queue for cancellation, pickup, negative-balance, and admin-created
        refund requests. Authorize owned components here; EZX settles ready
        funding groups automatically or waits for confirmed manual payout.
      </p>

      <Card>
        <CardContent className="pt-4 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select
              value={statusFilter}
              onValueChange={(v) =>
                setStatusFilter(v as PickupRefundRequestStatus)
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="PARTIALLY_APPROVED">
                  Partially approved
                </SelectItem>
                <SelectItem value="DENIED">Denied</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Source</Label>
            <Select
              value={sourceFilter}
              onValueChange={(v) => setSourceFilter(v as SourceFilter)}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All sources</SelectItem>
                <SelectItem value="SHIPMENT_CANCELLATION">
                  Cancellation
                </SelectItem>
                <SelectItem value="NEGATIVE_BALANCE">
                  Negative balance
                </SelectItem>
                <SelectItem value="PICKUP_DETACH">Pickup detach</SelectItem>
                <SelectItem value="PICKUP_CANCEL">Pickup cancel</SelectItem>
                <SelectItem value="PICKUP_RESCHEDULE">
                  Pickup reschedule
                </SelectItem>
                <SelectItem value="ADMIN_ORDER_REFUND">
                  Admin-created
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4 pb-4">
          {rows?.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-6 text-center">
              No {statusFilter.toLowerCase()} refund requests.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead>Shipment</TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="w-40"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rows ?? []).map((r) => {
                  const origin = originSummary(r);
                  const hasLines = r.lines.length > 0;
                  const isOpen = expanded[r.id] ?? false;
                  return (
                    <Fragment key={r.id}>
                      <TableRow
                        className={
                          requestedRefundId === r.id ? "bg-amber-50" : undefined
                        }
                      >
                        <TableCell className="w-8 p-1">
                          {hasLines && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              aria-label={
                                isOpen
                                  ? "Collapse components"
                                  : "Show components"
                              }
                              onClick={() =>
                                setExpanded((e) => ({ ...e, [r.id]: !isOpen }))
                              }
                            >
                              {isOpen ? (
                                <ChevronDown className="h-3 w-3" />
                              ) : (
                                <ChevronRight className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          <Badge
                            variant={SOURCE_VARIANT[r.source]}
                            className="text-[10px]"
                          >
                            {SOURCE_LABEL[r.source]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {requesterName(r)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {origin.primary}
                          {origin.secondary && (
                            <div className="text-[10px] text-muted-foreground">
                              {origin.secondary}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.shipment ? `#${r.shipment.shipmentNumber}` : "—"}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {formatCents(r.expectedAmountCents)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {formatDate(r.requestedAt)}
                        </TableCell>
                        <TableCell
                          className="text-xs max-w-xs truncate"
                          title={r.reason ?? ""}
                        >
                          {r.reason ?? "—"}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.status === "PENDING" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() =>
                                setExpanded((current) => ({
                                  ...current,
                                  [r.id]: true,
                                }))
                              }
                            >
                              Review components
                            </Button>
                          ) : (
                            <Badge
                              variant={STATUS_VARIANT[r.status]}
                              className="text-[10px]"
                            >
                              {STATUS_LABEL[r.status]}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                      {hasLines && isOpen && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={9} className="py-2 pl-10 pr-4">
                            <div className="space-y-1">
                              {r.lines.map((line) => {
                                const decider = lineDecider(line);
                                const requiresEmergencyOverride =
                                  r.requestedByInternalUser?.id === user?.id;
                                const awaitingCarrier =
                                  line.status === "PENDING" &&
                                  line.approverType === "PROVIDER";
                                return (
                                  <div
                                    key={line.id}
                                    className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                                  >
                                    <span className="w-28 font-medium">
                                      {REFUND_CHARGE_LABEL[line.chargeType]}
                                    </span>
                                    <span className="w-52 font-mono text-[10px]">
                                      Net {formatCents(line.amountNetCents)} ·
                                      Tax {formatCents(line.taxCents)} · Gross{" "}
                                      {formatCents(line.amountGrossCents)}
                                    </span>
                                    <span className="w-40 text-muted-foreground">
                                      {line.provider?.name ?? "EZX"}
                                    </span>
                                    <Badge
                                      variant={LINE_STATUS_VARIANT[line.status]}
                                      className="text-[10px]"
                                    >
                                      {line.status === "APPROVED" &&
                                      line.approvedGrossCents !== null &&
                                      line.approvedGrossCents !==
                                        line.amountGrossCents
                                        ? `APPROVED ${formatCents(line.approvedGrossCents)}`
                                        : line.status}
                                    </Badge>
                                    {decider && (
                                      <span className="text-[10px] text-muted-foreground">
                                        {decider}
                                      </span>
                                    )}
                                    {line.refundPayment && (
                                      <span className="text-[10px] text-green-700">
                                        Settlement total (shared across linked
                                        lines) · {line.refundPayment.method} ·{" "}
                                        {formatCents(
                                          line.refundPayment.amountCents
                                        )}
                                      </span>
                                    )}
                                    {awaitingCarrier && (
                                      <span className="text-[10px] text-muted-foreground italic">
                                        awaiting{" "}
                                        {line.provider?.name ?? "carrier"}
                                      </span>
                                    )}
                                    {line.status === "PENDING" && (
                                      <span className="flex gap-1 ml-auto">
                                        {!awaitingCarrier && (
                                          <>
                                            <Button
                                              size="sm"
                                              className="h-6 px-2 text-[10px]"
                                              aria-label={`Authorize ${REFUND_CHARGE_LABEL[line.chargeType]} component`}
                                              disabled={approveLine.isPending}
                                              onClick={async () => {
                                                if (requiresEmergencyOverride) {
                                                  setLineAction({
                                                    requestId: r.id,
                                                    line,
                                                    kind: "emergency-approve",
                                                  });
                                                  return;
                                                }
                                                try {
                                                  const result =
                                                    await approveLine.mutateAsync(
                                                      {
                                                        requestId: r.id,
                                                        lineId: line.id,
                                                      }
                                                    );
                                                  reportRefundLineSettlement(
                                                    "Component authorized",
                                                    result.settlement
                                                  );
                                                } catch (error) {
                                                  toast.error(
                                                    apiErrorMessage(
                                                      error,
                                                      "Couldn't approve line"
                                                    )
                                                  );
                                                }
                                              }}
                                            >
                                              Authorize
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-6 px-2 text-[10px]"
                                              aria-label={`Authorize partial ${REFUND_CHARGE_LABEL[line.chargeType]} component`}
                                              onClick={() =>
                                                setLineAction({
                                                  requestId: r.id,
                                                  line,
                                                  kind: requiresEmergencyOverride
                                                    ? "emergency-reduce"
                                                    : "reduce",
                                                })
                                              }
                                            >
                                              Authorize partial
                                            </Button>
                                          </>
                                        )}
                                        {!awaitingCarrier && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 px-2 text-[10px] text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                            aria-label={`Deny ${REFUND_CHARGE_LABEL[line.chargeType]} component`}
                                            onClick={() =>
                                              setLineAction({
                                                requestId: r.id,
                                                line,
                                                kind: "deny",
                                              })
                                            }
                                          >
                                            Deny
                                          </Button>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                              <div className="flex items-center gap-3 border-t pt-1 text-xs">
                                <span className="w-28 font-medium">Total</span>
                                <span className="w-20 font-mono font-semibold">
                                  {formatCents(
                                    r.lines.reduce(
                                      (sum, l) => sum + l.amountGrossCents,
                                      0
                                    )
                                  )}
                                </span>
                              </div>
                              {(r.returnTransports ?? []).map((transport) => (
                                <div
                                  key={transport.id}
                                  className="mt-3 rounded-md border border-violet-200 bg-violet-50/50 p-3 text-xs"
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="font-medium">
                                      Cancellation return settlement
                                    </p>
                                    <Badge variant="outline">
                                      {transport.status.replace(/_/g, " ")}
                                    </Badge>
                                  </div>
                                  <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-4">
                                    <span>
                                      Gross approved refund ·{" "}
                                      <strong>
                                        {formatCents(
                                          transport.grossRefundCents ??
                                            r.expectedAmountCents
                                        )}
                                      </strong>
                                    </span>
                                    <span>
                                      Return service ·{" "}
                                      <strong>
                                        {formatCents(
                                          transport.totalChargeCents
                                        )}
                                      </strong>
                                    </span>
                                    <span>
                                      Cost payer ·{" "}
                                      <strong>
                                        {transport.payer === "WAIVED"
                                          ? `WAIVED (${transport.waivedCostBearer ?? "EZX"})`
                                          : transport.payer}
                                      </strong>
                                    </span>
                                    <span>
                                      Customer cash movement ·{" "}
                                      <strong>
                                        {formatCents(
                                          transport.netCashMovementCents ?? 0
                                        )}
                                      </strong>
                                    </span>
                                  </div>
                                  {(transport.amountDueCents ?? 0) > 0 && (
                                    <p className="mt-2 font-medium text-amber-700">
                                      Customer amount due ·{" "}
                                      {formatCents(
                                        transport.amountDueCents ?? 0
                                      )}
                                    </p>
                                  )}
                                  {(transport.settlementGroups ?? []).length >
                                    0 && (
                                    <ul className="mt-2 space-y-1 border-t border-violet-200 pt-2 text-[11px] text-muted-foreground">
                                      {transport.settlementGroups.map(
                                        (group) => (
                                          <li key={group.id}>
                                            {group.rail.replace(/_/g, " ")} ·{" "}
                                            {group.status.replace(/_/g, " ")} ·{" "}
                                            Funding {group.fundingPaymentId} ·
                                            gross{" "}
                                            {formatCents(
                                              group.grossRefundCents
                                            )}
                                            {group.returnChargeOffsetCents > 0
                                              ? ` − return ${formatCents(group.returnChargeOffsetCents)}`
                                              : ""}
                                            {` = ${formatCents(group.externalRefundCents)} moved`}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  )}
                                  {transport.settlementError && (
                                    <p className="mt-2 text-red-700">
                                      Settlement needs attention:{" "}
                                      {transport.settlementError}
                                    </p>
                                  )}
                                </div>
                              ))}
                              <div className="mt-3 grid gap-3 border-t pt-3 lg:grid-cols-2">
                                <div className="rounded-md border p-3">
                                  <p className="text-xs font-medium">
                                    Request and settlement
                                  </p>
                                  <p className="mt-1 text-[11px] text-muted-foreground">
                                    Requested by {requesterName(r)}. Component
                                    approvers are shown above and cannot
                                    self-approve without an audited emergency
                                    override.
                                  </p>
                                  <p className="mt-2 text-xs">
                                    {r.settledAt
                                      ? `Settlement completed ${formatDate(r.settledAt)}`
                                      : r.settlementError
                                        ? `Settlement needs attention: ${r.settlementError}`
                                        : (r.manualPayouts ?? []).some(
                                              (payout) =>
                                                payout.status ===
                                                "AWAITING_PAYOUT"
                                            )
                                          ? "Authorized · awaiting manual payout confirmation"
                                          : "Awaiting component authorization or automatic settlement"}
                                  </p>
                                  {(r.manualPayouts ?? []).map((payout) => (
                                    <div
                                      key={payout.id}
                                      className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/50 p-2"
                                    >
                                      <span>
                                        {payout.method.replace(/_/g, " ")} ·{" "}
                                        {formatCents(payout.amountCents)} payout
                                        {payout.grossRefundCents !== null
                                          ? ` (${formatCents(payout.grossRefundCents)} gross refund − ${formatCents(payout.returnChargeOffsetCents ?? 0)} return service)`
                                          : ""}{" "}
                                        · {payout.status.replace(/_/g, " ")}
                                      </span>
                                      {payout.status !== "PAID" && (
                                        <Button
                                          size="sm"
                                          className="h-7 px-2 text-[10px]"
                                          onClick={() =>
                                            setPayoutAction(payout)
                                          }
                                        >
                                          {payout.status === "AWAITING_PAYOUT"
                                            ? "Confirm payout"
                                            : "Reconcile payout"}
                                        </Button>
                                      )}
                                      {payout.status === "PAID" && (
                                        <span className="text-[10px] text-green-700">
                                          Paid{" "}
                                          {payout.paidAt
                                            ? formatDate(payout.paidAt)
                                            : ""}
                                          {payout.externalReference
                                            ? ` · ${payout.externalReference}`
                                            : ""}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <div className="rounded-md border p-3">
                                  <p className="text-xs font-medium">
                                    Assignments and settlement groups
                                  </p>
                                  {(r.settlementGroups ?? []).length > 0 && (
                                    <ul
                                      aria-label={`Settlement groups for refund ${r.id}`}
                                      className="mt-2 space-y-1 border-b pb-2"
                                    >
                                      {r.settlementGroups.map((group) => (
                                        <li
                                          key={group.id}
                                          className="text-[11px]"
                                        >
                                          {group.rail.replace(/_/g, " ")} ·{" "}
                                          {group.status.replace(/_/g, " ")} ·{" "}
                                          {formatCents(group.grossRefundCents)}
                                          {group.errorCode
                                            ? ` · ${group.errorCode.replace(/_/g, " ")}`
                                            : ""}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {(r.actionEvents ?? []).length === 0 ? (
                                    <p className="mt-1 text-[11px] text-muted-foreground">
                                      No action events recorded.
                                    </p>
                                  ) : (
                                    <ul className="mt-2 space-y-1">
                                      {(r.actionEvents ?? [])
                                        .slice(0, 8)
                                        .map((event) => (
                                          <li
                                            key={event.id}
                                            className="flex flex-wrap items-center gap-1 text-[11px]"
                                          >
                                            <span>
                                              <span className="font-medium">
                                                {event.type.replace(/_/g, " ")}
                                              </span>{" "}
                                              · {event.audience}
                                              {event.actionNeeded
                                                ? " · action needed"
                                                : " · resolved"}
                                              {event.reason
                                                ? ` · ${event.reason}`
                                                : ""}
                                              {event.details?.groupKey
                                                ? ` · funding group ${String(event.details.groupKey)}`
                                                : ""}
                                              {` · ${formatDate(event.resolvedAt ?? event.createdAt)}`}
                                            </span>
                                            {event.actionNeeded &&
                                              (event.type ===
                                                "SETTLEMENT_FAILED" ||
                                                event.type ===
                                                  "SETTLEMENT_UNKNOWN") && (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="h-6 px-2 text-[10px]"
                                                  onClick={() =>
                                                    setSettlementAction(event)
                                                  }
                                                >
                                                  {event.type ===
                                                  "SETTLEMENT_UNKNOWN"
                                                    ? "Investigate outcome"
                                                    : "Retry failed settlement"}
                                                </Button>
                                              )}
                                          </li>
                                        ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {lineAction && (
        <RefundLineActionDialog
          action={lineAction}
          onClose={() => setLineAction(null)}
        />
      )}
      {payoutAction && (
        <ManualPayoutDialog
          payout={payoutAction}
          onClose={() => setPayoutAction(null)}
        />
      )}
      {settlementAction && (
        <SettlementReconciliationDialog
          event={settlementAction}
          onClose={() => setSettlementAction(null)}
        />
      )}
    </div>
  );
}

function SettlementReconciliationDialog({
  event,
  onClose,
}: {
  event: RefundActionEventRow;
  onClose: () => void;
}) {
  const unknown = event.type === "SETTLEMENT_UNKNOWN";
  const [reason, setReason] = useState("");
  const [evidenceReference, setEvidenceReference] = useState("");
  const reconcile = useReconcileRefundSettlementIssue();

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Investigation reason is required");
      return;
    }
    try {
      const result = await reconcile.mutateAsync({
        eventId: event.id,
        action: unknown ? "RETRY_UNKNOWN_SAFELY" : "RETRY_FAILED",
        reason: reason.trim(),
        evidenceReference: evidenceReference.trim() || undefined,
      });
      toast.success("Settlement retry submitted", {
        description: settlementMessage(result.settlement),
      });
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Settlement was not retried"));
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {unknown ? "Reconcile unknown refund" : "Retry refund settlement"}
          </DialogTitle>
          <DialogDescription>
            {unknown
              ? "Replay the held processor attempt with its original idempotency key."
              : "Record why resuming this settlement is safe."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {unknown
              ? "The processor outcome is unknown. This retries the exact same provider attempt, so a refund already accepted by the provider is reconciled rather than duplicated."
              : "A failure can occur at the processor or while completing accounting after confirmation. The server resumes from the recorded settlement state. Already confirmed refunds are not sent again."}
          </p>
          <div className="space-y-1">
            <Label htmlFor="settlement-reconcile-reason">
              Investigation and retry reason
            </Label>
            <Textarea
              id="settlement-reconcile-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          {unknown && (
            <div className="space-y-1">
              <Label htmlFor="settlement-evidence-reference">
                Evidence reference
              </Label>
              <Input
                id="settlement-evidence-reference"
                value={evidenceReference}
                onChange={(event) => setEvidenceReference(event.target.value)}
                placeholder="Provider event, dashboard URL, or support case"
              />
              <p className="text-xs text-muted-foreground">
                This reference becomes part of the refund audit trail.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={reconcile.isPending}
          >
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={reconcile.isPending}>
            {reconcile.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {unknown ? "Safely retry same attempt" : "Retry settlement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualPayoutDialog({
  payout,
  onClose,
}: {
  payout: ManualRefundPayoutRow;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"confirm" | "reconcile">(
    payout.status === "AWAITING_PAYOUT" ? "confirm" : "reconcile"
  );
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(
    new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 16)
  );
  const [note, setNote] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [reconcileAction, setReconcileAction] = useState<
    "MARK_FAILED" | "FLAG_RECONCILIATION" | "REOPEN"
  >(payout.status === "AWAITING_PAYOUT" ? "FLAG_RECONCILIATION" : "REOPEN");
  const [reconcileReason, setReconcileReason] = useState("");
  const confirm = useConfirmManualRefundPayout();
  const reconcile = useReconcileManualRefundPayout();
  const isPending = confirm.isPending || reconcile.isPending;

  const submitConfirmation = async () => {
    if (!reference.trim() || !paidAt) {
      toast.error("Payout reference and paid-at time are required");
      return;
    }
    try {
      await confirm.mutateAsync({
        payoutId: payout.id,
        method: payout.method,
        amountCents: payout.amountCents,
        externalReference: reference.trim(),
        paidAt: new Date(paidAt).toISOString(),
        note: note.trim() || undefined,
        evidenceUrl: evidenceUrl.trim() || undefined,
      });
      toast.success("Manual payout confirmed", {
        description: `${formatCents(payout.amountCents)} is now recorded as paid.`,
      });
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Manual payout was not confirmed"));
    }
  };

  const submitReconciliation = async () => {
    if (!reconcileReason.trim()) {
      toast.error("Reconciliation reason is required");
      return;
    }
    try {
      await reconcile.mutateAsync({
        payoutId: payout.id,
        action: reconcileAction,
        reason: reconcileReason.trim(),
      });
      toast.success("Manual payout state updated");
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Payout could not be reconciled"));
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && !isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Manual refund payout</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border p-3 text-sm">
            <p>
              {payout.method.replace(/_/g, " ")} ·{" "}
              <span className="font-semibold">
                {formatCents(payout.amountCents)} {payout.currency}
              </span>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Funding payment{" "}
              {payout.fundingPayment.referenceNumber ?? payout.fundingPaymentId}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Authorization alone is not a refund. Confirm only after the exact
              payout has actually been sent.
            </p>
          </div>

          {payout.status === "AWAITING_PAYOUT" && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={mode === "confirm" ? "default" : "outline"}
                onClick={() => setMode("confirm")}
              >
                Confirm actual payout
              </Button>
              <Button
                size="sm"
                variant={mode === "reconcile" ? "default" : "outline"}
                onClick={() => setMode("reconcile")}
              >
                Flag / reconcile
              </Button>
            </div>
          )}

          {mode === "confirm" ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="payout-reference">Payout reference</Label>
                <Input
                  id="payout-reference"
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="payout-paid-at">Paid at</Label>
                <Input
                  id="payout-paid-at"
                  type="datetime-local"
                  value={paidAt}
                  onChange={(event) => setPaidAt(event.target.value)}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="payout-note">Note (optional)</Label>
                <Textarea
                  id="payout-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="payout-evidence">Evidence URL (optional)</Label>
                <Input
                  id="payout-evidence"
                  type="url"
                  value={evidenceUrl}
                  onChange={(event) => setEvidenceUrl(event.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Reconciliation action</Label>
                <Select
                  value={reconcileAction}
                  onValueChange={(value) =>
                    setReconcileAction(
                      value as "MARK_FAILED" | "FLAG_RECONCILIATION" | "REOPEN"
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FLAG_RECONCILIATION">
                      Outcome unknown — investigate
                    </SelectItem>
                    <SelectItem value="MARK_FAILED">
                      Confirmed not paid
                    </SelectItem>
                    <SelectItem value="REOPEN">
                      Reopen for confirmation
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="payout-reconcile-reason">Reason</Label>
                <Textarea
                  id="payout-reconcile-reason"
                  value={reconcileReason}
                  onChange={(event) => setReconcileReason(event.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Never reopen an unknown payout until evidence confirms that no
                money moved. This avoids a blind duplicate refund.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              void (mode === "confirm"
                ? submitConfirmation()
                : submitReconciliation())
            }
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "confirm" ? "Confirm paid" : "Update payout state"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
